import { useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Tags } from 'lucide-react';
import { COUPON_TYPES, type Coupon, type CouponType } from '@tailoring-crm/shared';
import { EmptyState } from '../components/feedback/empty-state.js';
import { PageHeader } from '../components/layout/page-header.js';
import { Badge, Button, Input, Label, ListSkeleton, useToast } from '@tailoring-crm/ui';
import { isApiRequestError } from '../lib/api/errors.js';
import { useAuth } from '../lib/auth/auth-context.js';
import { useCoupons, useCreateCoupon, useUpdateCoupon } from '../lib/coupons/use-coupons.js';
import { formatCurrency } from '../lib/format.js';

/**
 * The shop's own codes.
 *
 * Under Settings rather than in the primary navigation, and that placement IS the product
 * decision: setting a coupon up is a standing commitment an owner makes a few times a year,
 * while typing one somebody read off a slip happens at the counter, on the booking form, where
 * the code is actually used. Putting both in one screen would have made the rare thing loud and
 * the common thing a navigation away from where it is needed.
 *
 * EVERY CARD LEADS WITH WHAT THE CODE HAS COST. "20% off, capped at ₹500" is what the owner
 * decided; "used 47 times, ₹18,400 given away" is what actually happened, and it is the only
 * figure that answers whether the lever was worth pulling.
 *
 * There is no delete. A coupon is switched off, so the bills that used it still point at
 * something that explains them.
 */
export function CouponsPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();
  const [creating, setCreating] = useState(false);

  // The API refuses both halves to a karigar, and writing to anybody but the owner.
  const canRead = role === 'owner' || role === 'manager';
  const canWrite = role === 'owner';

  const coupons = useCoupons({}, { enabled: canRead });
  const items = coupons.data?.items ?? [];

  if (!canRead) {
    return (
      <>
        <PageHeader title={t('coupons.title')} />
        <EmptyState
          icon={Tags}
          title={t('coupons.noAccessTitle')}
          description={t('coupons.noAccessHint')}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('coupons.title')}
        description={t('coupons.description')}
        action={
          canWrite ? (
            <Button onClick={() => setCreating(!creating)}>{t('coupons.add')}</Button>
          ) : undefined
        }
      />

      <div className="flex flex-col gap-4">
        {creating && canWrite ? <CouponForm onDone={() => setCreating(false)} /> : null}

        {coupons.isPending ? <ListSkeleton rows={3} /> : null}

        {!coupons.isPending && items.length === 0 ? (
          <EmptyState
            icon={Tags}
            title={t('coupons.emptyTitle')}
            description={t('coupons.emptyDescription')}
          />
        ) : null}

        {items.map((coupon) => (
          <CouponCard key={coupon.id} coupon={coupon} canWrite={canWrite} />
        ))}
      </div>
    </>
  );
}

/** "20% off, up to ₹500" — what the owner decided, in one line. */
function describeCoupon(coupon: Coupon, t: TFunction): string {
  const head =
    coupon.type === 'percent'
      ? t('coupons.percentOff', { value: coupon.value })
      : t('coupons.amountOff', { amount: formatCurrency(coupon.value) });
  const cap =
    coupon.type === 'percent' && coupon.maxDiscountPaise !== null
      ? ` ${t('coupons.cappedAt', { amount: formatCurrency(coupon.maxDiscountPaise) })}`
      : '';
  const minimum =
    coupon.minOrderPaise > 0
      ? ` ${t('coupons.onBillsOver', { amount: formatCurrency(coupon.minOrderPaise) })}`
      : '';
  return `${head}${cap}${minimum}`;
}

function CouponCard({ coupon, canWrite }: { coupon: Coupon; canWrite: boolean }): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const update = useUpdateCoupon();

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-lg font-semibold">{coupon.code}</p>
          <p className="text-sm text-muted-foreground">{describeCoupon(coupon, t)}</p>
          {coupon.description ? <p className="text-sm">{coupon.description}</p> : null}
        </div>
        <Badge variant={coupon.active ? 'default' : 'secondary'}>
          {t(coupon.active ? 'coupons.on' : 'coupons.off')}
        </Badge>
      </div>

      {/* What it actually cost, which is the only figure that answers whether it was worth it. */}
      <p className="text-sm">
        {t('coupons.usage', {
          count: coupon.timesRedeemed,
          amount: formatCurrency(coupon.discountGivenPaise),
        })}
        {coupon.usageLimit !== null ? ` ${t('coupons.ofLimit', { limit: coupon.usageLimit })}` : ''}
      </p>

      {canWrite ? (
        <div>
          <Button
            size="sm"
            variant="outline"
            disabled={update.isPending}
            onClick={() => {
              void (async () => {
                try {
                  await update.mutateAsync({ couponId: coupon.id, active: !coupon.active });
                  toast({
                    title: t(coupon.active ? 'coupons.switchedOff' : 'coupons.switchedOn'),
                    variant: 'success',
                  });
                } catch {
                  toast({ title: t('error.unexpected'), variant: 'destructive' });
                }
              })();
            }}
          >
            {t(coupon.active ? 'coupons.switchOff' : 'coupons.switchOn')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function CouponForm({ onDone }: { onDone: () => void }): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const create = useCreateCoupon();

  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<CouponType>('percent');
  const [value, setValue] = useState('10');
  const [maxDiscount, setMaxDiscount] = useState('');
  const [minOrder, setMinOrder] = useState('');
  const [usageLimit, setUsageLimit] = useState('');

  /** Rupees in the box, paise on the wire. Money is an integer everywhere it is stored. */
  const paise = (rupees: string): number => Math.round((Number(rupees) || 0) * 100);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    void (async () => {
      try {
        await create.mutateAsync({
          code,
          description: description.trim() || null,
          type,
          // A percentage is a whole number; a fixed amount is rupees typed and paise sent.
          value: type === 'percent' ? Number(value) || 0 : paise(value),
          minOrderPaise: paise(minOrder),
          maxDiscountPaise: type === 'percent' && maxDiscount ? paise(maxDiscount) : null,
          usageLimit: usageLimit ? Number(usageLimit) : null,
          active: true,
        });
        toast({ title: t('coupons.created'), variant: 'success' });
        onDone();
      } catch (caught) {
        toast({
          title: isApiRequestError(caught) ? caught.message : t('error.unexpected'),
          variant: 'destructive',
        });
      }
    })();
  };

  return (
    <form className="flex flex-col gap-3 rounded-lg border border-border p-4" onSubmit={submit}>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="coupon-code">{t('coupons.code')}</Label>
          <Input
            id="coupon-code"
            value={code}
            required
            maxLength={24}
            placeholder="DEEPAVALI20"
            onChange={(event) => setCode(event.target.value.toUpperCase())}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="coupon-description">{t('coupons.descriptionLabel')}</Label>
          <Input
            id="coupon-description"
            value={description}
            maxLength={200}
            placeholder={t('coupons.descriptionPlaceholder')}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="coupon-type">{t('coupons.typeLabel')}</Label>
          <select
            id="coupon-type"
            value={type}
            onChange={(event) => setType(event.target.value as CouponType)}
            className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
          >
            {COUPON_TYPES.map((one) => (
              <option key={one} value={one}>
                {t(`coupons.type.${one}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="coupon-value">
            {t(type === 'percent' ? 'coupons.percentValue' : 'coupons.amountValue')}
          </Label>
          <Input
            id="coupon-value"
            inputMode="decimal"
            value={value}
            required
            onChange={(event) => setValue(event.target.value)}
          />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        {/* Only for a percentage: an uncapped 20% on a ₹40,000 bridal order is ₹8,000 nobody
            meant to give away, so the box is put in front of the owner rather than hidden. */}
        {type === 'percent' ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="coupon-cap">{t('coupons.cap')}</Label>
            <Input
              id="coupon-cap"
              inputMode="decimal"
              value={maxDiscount}
              placeholder={t('coupons.capPlaceholder')}
              onChange={(event) => setMaxDiscount(event.target.value)}
            />
          </div>
        ) : null}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="coupon-min">{t('coupons.minimum')}</Label>
          <Input
            id="coupon-min"
            inputMode="decimal"
            value={minOrder}
            placeholder={t('coupons.minimumPlaceholder')}
            onChange={(event) => setMinOrder(event.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="coupon-limit">{t('coupons.usageLimit')}</Label>
          <Input
            id="coupon-limit"
            inputMode="numeric"
            value={usageLimit}
            placeholder={t('coupons.usageLimitPlaceholder')}
            onChange={(event) => setUsageLimit(event.target.value)}
          />
        </div>
      </div>

      <div className="flex gap-2">
        <Button type="submit" disabled={create.isPending}>
          {t('common.save')}
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}

export default CouponsPage;
